/**
 * Explore Bar view (POS2-883): vertical columns per time bucket, stacked segments
 * per split series (single segment when unsplit). Stacked heights sum to the engine's
 * row totals — buildChartData carries every col key including the remainder tail.
 */
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";

import { ChartContainer, ChartTooltip } from "@/components/ui/chart";
import { useTranslation } from "@/i18n/use-translation";
import { EXPLORE_MEASURES, precedingWindow } from "@/lib/explore-engine";
import {
  buildChartData,
  cellDeltas,
  formatBucketTick,
  tickInterval,
  tickSpan,
  windowRowKeys,
} from "@/lib/explore-chart";

import {
  BucketTick,
  ExploreLegend,
  ExploreTooltip,
  seriesChartConfig,
  type ExploreViewProps,
} from "./chart-parts";

/** Widest a single column may draw (POS2-1050). Without a cap recharts hands each category its
 * whole band, so a one-bucket window — which is also where a Line falls back to this view —
 * fills the panel edge to edge and reads as a rendering fault rather than as one data point. */
const MAX_BAR_WIDTH = 96;

export function ExploreBar({
  pivot,
  priorPivot,
  measure,
  currency,
  gran,
  split,
  window,
  timezone,
}: ExploreViewProps) {
  const { locale, t } = useTranslation();
  const { rows, series } = buildChartData(
    pivot,
    measure,
    windowRowKeys(pivot, window, gran, timezone),
  );
  const fmt = (n: number) => EXPLORE_MEASURES[measure].format(n, { currency, locale, t });
  const interval = tickInterval(rows.length);
  // Widen the tick labels to whatever calendar the plotted buckets span (POS2-1068).
  const span = tickSpan(rows.map((r) => r.key));
  // Per-series Δ from the shared derivation — same numbers as the Table, first-bucket baseline
  // from the preceding window included (POS2-1038).
  const deltas = cellDeltas(
    pivot,
    measure,
    window,
    gran,
    timezone,
    priorPivot,
    precedingWindow(window),
  );

  return (
    <div className="flex flex-col" data-testid="explore-view-bar">
      <ChartContainer config={seriesChartConfig(series)} className="aspect-auto h-[420px] w-full">
        <BarChart data={rows} margin={{ left: 12, right: 12, top: 8 }}>
          <CartesianGrid vertical={false} />
          {gran === "daily" ? (
            <XAxis
              dataKey="key"
              tickLine={false}
              axisLine={false}
              interval={interval}
              height={44}
              tickMargin={6}
              tick={<BucketTick span={span} />}
            />
          ) : (
            <XAxis
              dataKey="key"
              tickLine={false}
              axisLine={false}
              interval={interval}
              tickMargin={8}
              minTickGap={16}
              tickFormatter={(v: string) => formatBucketTick(v, gran, span, { locale, t })}
            />
          )}
          <YAxis hide domain={[0, "auto"]} />
          <ChartTooltip
            cursor={{ fill: "rgba(17,24,39,0.04)" }}
            content={<ExploreTooltip series={series} gran={gran} format={fmt} deltas={deltas} />}
          />
          {series.map((s, i) => (
            <Bar
              key={s.key}
              dataKey={s.key}
              stackId="stack"
              fill={s.color}
              maxBarSize={MAX_BAR_WIDTH}
              // Round only the topmost segment of each column.
              radius={i === series.length - 1 ? [2, 2, 0, 0] : 0}
              isAnimationActive={false}
            />
          ))}
        </BarChart>
      </ChartContainer>
      {split !== "none" && <ExploreLegend series={series} />}
    </div>
  );
}
